import React, { useState, useEffect, useMemo, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import {
  Building2,
  Loader2,
  CheckCircle,
  AlertCircle,
  ArrowLeft,
  Copy,
  Zap,
  X,
  Calendar,
  GitBranch,
} from "lucide-react";
import Cookies from "js-cookie";
import Sidebar from "@/components/Sidebar";
import { TIMEZONE_OPTIONS, getDetectedTimezone } from "@/pages/integrations/shared/timezoneUtils";
import TimezoneField from "@/pages/integrations/shared/TimezoneField";
import { getClientAuthHeaders, getClientAuthToken } from "@/lib/apiAuth";
import { DASHBOARD_ROUTES } from "@/lib/dashboardRoutes";
import { WEBSITE_LEAD_INTERCEPTOR_COPY } from "@/constants/websiteLeadInterceptor";

const BACKEND_URL = (process.env.REACT_APP_BACKEND_URL || "").replace(/\/$/, "");

/**
 * Integration API (JWT): uses REACT_APP_BACKEND_URL — must match the FastAPI host (same origin/CORS as dashboard).
 * Auth: cookie `token` (Bearer). Backend needs MONGO_*, JWT_SECRET, ENCRYPTION_KEY for GHL credentials.
 *
 * - GET  {BACKEND_URL}/api/integrations/ghl — get or create row; returns { status, id, integration }
 * - POST {BACKEND_URL}/api/integrations/ghl/save — body: { api_key, timezone, location_id? }; returns { status, id }
 * - POST {BACKEND_URL}/api/integrations/ghl/test_mode — body: { activate, contact_id?, phone?, email? }; returns { status, test_mode }
 *
 * Webhook URLs shown to the user / copied to clipboard:
 * {BACKEND_URL}/api/webhooks/ghl/{event_type}/{integrationId}
 * (event_type: contact_created | appointment_status | customer_replied).
 * Receivers are implemented on the server at POST /api/webhooks/ghl/...
 */
const GHL_INTEGRATION_URL = `${BACKEND_URL}/api/integrations/ghl`;
const GHL_SAVE_URL = `${BACKEND_URL}/api/integrations/ghl/save`;
const GHL_TEST_MODE_SAVE_URL = `${BACKEND_URL}/api/integrations/ghl/test_mode`;
const GHL_TEST_CONNECTION_URL = `${BACKEND_URL}/api/integrations/ghl/test_connection`;
const GHL_LIST_CALENDARS_URL = `${BACKEND_URL}/api/integrations/ghl/list_calendars`;
const GHL_LIST_PIPELINES_URL = `${BACKEND_URL}/api/integrations/ghl/list_pipelines`;
const GHL_LIST_CUSTOM_FIELDS_URL = `${BACKEND_URL}/api/integrations/ghl/list_custom_fields`;
const GHL_CREATE_CUSTOM_FIELD_URL = `${BACKEND_URL}/api/integrations/ghl/create_custom_field`;
const GHL_SAVE_OPPORTUNITY_ROUTING_URL = `${BACKEND_URL}/api/integrations/ghl/save_opportunity_routing`;
const PIPELINE_MODAL_STEP = {
  pipeline: "pipeline",
  customField: "customField",
};
const BOT_SETTINGS_URL = `${BACKEND_URL}/api/bot/settings`;
const PUT_ROOFING_TEAM_CALENDARS_URL = `${BACKEND_URL}/api/bot/settings/roofing_team_calendars`;
const PUT_RENOVATION_TEAM_CALENDARS_URL = `${BACKEND_URL}/api/bot/settings/renovation_team_calendars`;

const ACCENT = "#01B47A";

/** Normalize FastAPI error bodies (detail string | validation array | message). */
const formatSaveError = (data, status) => {
  if (data?.detail != null) {
    if (typeof data.detail === "string") return data.detail;
    if (Array.isArray(data.detail)) {
      return data.detail
        .map((item) => {
          if (item == null) return "";
          if (typeof item === "string") return item;
          if (typeof item === "object") {
            return item.msg || item.message || JSON.stringify(item);
          }
          return String(item);
        })
        .filter(Boolean)
        .join("; ");
    }
  }
  if (data?.message && typeof data.message === "string") return data.message;
  if (status === 404) return "Save endpoint is not available yet. Please try again later.";
  return "Failed to save configuration. Please try again.";
};

const buildWebhookUrls = (integrationId) => {
  if (!integrationId) {
    return {
      contactCreated: "",
      appointmentStatus: "",
      customerReplied: "",
    };
  }
  const base = `${BACKEND_URL}/api/webhooks/ghl`;
  return {
    contactCreated: `${base}/contact_created/${integrationId}`,
    appointmentStatus: `${base}/appointment_status/${integrationId}`,
    customerReplied: `${base}/customer_replied/${integrationId}`,
  };
};

const teamMemberLabel = (entry, index) => {
  const e = entry && typeof entry === "object" ? entry : {};
  const s = (v) => (typeof v === "string" ? v.trim() : "");
  return (
    s(e.staff_name) ||
    s(e.role) ||
    s(e.name) ||
    s(e.attendee) ||
    `Team member ${index + 1}`
  );
};

const copyToClipboard = async (text) => {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    const textArea = document.createElement("textarea");
    textArea.value = text;
    textArea.style.position = "fixed";
    textArea.style.left = "-9999px";
    document.body.appendChild(textArea);
    textArea.select();
    try {
      document.execCommand("copy");
      return true;
    } catch {
      return false;
    } finally {
      document.body.removeChild(textArea);
    }
  }
};

const WebhookUrlRow = ({
  label,
  value,
  isLoading,
  hint,
  copied,
  onCopy,
  testIdPrefix,
}) => {
  const display = isLoading ? "Loading..." : value || "—";
  const canCopy = Boolean(value && !isLoading);

  return (
    <div className="space-y-2">
      <label className="block text-[#F3F4F6] text-sm font-body">{label}</label>
      <div className="flex items-center gap-2">
        <input
          type="text"
          value={display}
          readOnly
          className="flex-1 bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#9CA3AF] font-body text-sm cursor-default focus:outline-none select-all"
          data-testid={`${testIdPrefix}-input`}
        />
        <button
          type="button"
          onClick={onCopy}
          disabled={!canCopy}
          className={`flex items-center gap-2 px-4 py-3 rounded-lg border transition-all duration-300 font-body text-sm whitespace-nowrap ${
            copied
              ? "bg-green-500/10 border-green-500/30 text-green-400"
              : canCopy
              ? "bg-white/5 border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20"
              : "bg-white/5 border-white/10 text-[#9CA3AF]/40 cursor-not-allowed"
          }`}
          data-testid={`${testIdPrefix}-copy`}
        >
          {copied ? (
            <>
              <CheckCircle className="w-4 h-4" />
              Copied
            </>
          ) : (
            <>
              <Copy className="w-4 h-4" />
              Copy
            </>
          )}
        </button>
      </div>
      {hint && <p className="text-[#9CA3AF]/70 text-xs font-body">{hint}</p>}
    </div>
  );
};

const GoHighLevel = () => {
  const navigate = useNavigate();
  const [integrationId, setIntegrationId] = useState(null);
  const [isLoadingIntegration, setIsLoadingIntegration] = useState(true);
  const [apiKey, setApiKey] = useState("");
  const [locationId, setLocationId] = useState("");
  const [selectedTimezone, setSelectedTimezone] = useState("");
  const [isSaving, setIsSaving] = useState(false);
  const [formError, setFormError] = useState("");
  const [success, setSuccess] = useState(false);
  const [copiedField, setCopiedField] = useState(null);

  const [hasApiKey, setHasApiKey] = useState(false);
  const [integrationStatus, setIntegrationStatus] = useState("pending_auth");
  const [isEnabled, setIsEnabled] = useState(false);
  const [isTogglingStatus, setIsTogglingStatus] = useState(false);
  const [statusNotification, setStatusNotification] = useState(null);
  const [testModeActivate, setTestModeActivate] = useState(false);
  const [testModeContactId, setTestModeContactId] = useState("");
  const [testModePhone, setTestModePhone] = useState("");
  const [testModeEmail, setTestModeEmail] = useState("");
  const [isSavingTestMode, setIsSavingTestMode] = useState(false);
  const [testModeError, setTestModeError] = useState("");
  const [testModeSuccess, setTestModeSuccess] = useState(false);
  const [isTestModalOpen, setIsTestModalOpen] = useState(false);
  const [isTestingConnection, setIsTestingConnection] = useState(false);
  const [testResult, setTestResult] = useState(null);

  const [isCalendarModalOpen, setIsCalendarModalOpen] = useState(false);
  const [isCalendarModalLoading, setIsCalendarModalLoading] = useState(false);
  const [isCalendarSaving, setIsCalendarSaving] = useState(false);
  const [ghlCalendars, setGhlCalendars] = useState([]);
  const [teamCalendarRows, setTeamCalendarRows] = useState([]);
  const [calendarBotType, setCalendarBotType] = useState("");
  const [calendarModalError, setCalendarModalError] = useState("");
  const [calendarModalSuccess, setCalendarModalSuccess] = useState("");

  const [isPipelineModalOpen, setIsPipelineModalOpen] = useState(false);
  const [isPipelineModalLoading, setIsPipelineModalLoading] = useState(false);
  const [pipelineModalError, setPipelineModalError] = useState("");
  const [pipelineModalInfo, setPipelineModalInfo] = useState("");
  const [ghlPipelines, setGhlPipelines] = useState([]);
  const [selectedPipelineId, setSelectedPipelineId] = useState("");
  const [selectedStageId, setSelectedStageId] = useState("");
  const [pipelineModalStep, setPipelineModalStep] = useState(PIPELINE_MODAL_STEP.pipeline);
  const [isCustomFieldsLoading, setIsCustomFieldsLoading] = useState(false);
  const [ghlCustomFields, setGhlCustomFields] = useState([]);
  const [selectedCustomFieldId, setSelectedCustomFieldId] = useState("");
  const [customFieldTypeWarning, setCustomFieldTypeWarning] = useState("");
  const [newCustomFieldName, setNewCustomFieldName] = useState("");
  const [newCustomFieldPlaceholder, setNewCustomFieldPlaceholder] = useState("");
  const [isCreatingCustomField, setIsCreatingCustomField] = useState(false);
  const [customFieldCreateSuccess, setCustomFieldCreateSuccess] = useState("");
  const [isPipelineRoutingSaving, setIsPipelineRoutingSaving] = useState(false);
  const [savedOpportunityRouting, setSavedOpportunityRouting] = useState(null);
  const [pipelineRoutingPageSuccess, setPipelineRoutingPageSuccess] = useState("");

  const detectedTimezone = useMemo(() => getDetectedTimezone(), []);

  const canConnectGhlCalendars = Boolean(hasApiKey && locationId.trim());
  const canConnectGhlPipelines = Boolean(hasApiKey && locationId.trim());

  const selectedPipeline = useMemo(
    () => ghlPipelines.find((p) => p.id === selectedPipelineId) || null,
    [ghlPipelines, selectedPipelineId]
  );

  const stagesForSelectedPipeline = useMemo(() => {
    const raw = selectedPipeline?.stages;
    if (!Array.isArray(raw)) return [];
    return [...raw].sort((a, b) => {
      const pa = a?.position;
      const pb = b?.position;
      if (typeof pa === "number" && typeof pb === "number" && pa !== pb) return pa - pb;
      if (typeof pa === "number" && typeof pb !== "number") return -1;
      if (typeof pa !== "number" && typeof pb === "number") return 1;
      const na = String(a?.name || "");
      const nb = String(b?.name || "");
      return na.localeCompare(nb);
    });
  }, [selectedPipeline]);

  const selectedStage = useMemo(
    () => stagesForSelectedPipeline.find((s) => s.id === selectedStageId) || null,
    [stagesForSelectedPipeline, selectedStageId]
  );

  const selectedCustomField = useMemo(
    () => ghlCustomFields.find((f) => f.id === selectedCustomFieldId) || null,
    [ghlCustomFields, selectedCustomFieldId]
  );

  const webhooks = useMemo(() => buildWebhookUrls(integrationId), [integrationId]);

  const loadIntegration = useCallback(async ({ trackLoading = true } = {}) => {
    try {
      const token = getClientAuthToken();
    if (!token) {
        if (trackLoading) setIsLoadingIntegration(false);
        return;
      }

      const response = await fetch(GHL_INTEGRATION_URL, {
        method: "GET",
        headers: getClientAuthHeaders(),
      });

      if (response.ok) {
        const data = await response.json();
        if (data.id) setIntegrationId(data.id);

        setHasApiKey(Boolean(data.has_api_key));
        const st = data.integration?.status || "pending_auth";
        setIntegrationStatus(st);
        setIsEnabled(st !== "paused");

        const tz = data.integration?.credentials?.timezone;
        if (tz && TIMEZONE_OPTIONS.some((o) => o.value === tz)) {
          setSelectedTimezone(tz);
        } else if (trackLoading) {
          const d = getDetectedTimezone();
          if (d && TIMEZONE_OPTIONS.some((o) => o.value === d)) {
            setSelectedTimezone(d);
          }
        }

        const creds = data.integration?.credentials;
        const lid = creds?.location_id;
        setLocationId(typeof lid === "string" ? lid : "");

        const savedPipeline = creds?.pipeline;
        const savedField = creds?.bot_comment_custom_field;
        if (
          savedPipeline &&
          typeof savedPipeline === "object" &&
          String(savedPipeline.pipeline_id || "").trim()
        ) {
          setSavedOpportunityRouting({
            pipeline: savedPipeline,
            bot_comment_custom_field:
              savedField && typeof savedField === "object" ? savedField : null,
          });
        } else {
          setSavedOpportunityRouting(null);
        }

        const tm = data.integration?.test_mode;
        if (tm && typeof tm === "object") {
          setTestModeActivate(Boolean(tm.activate));
          setTestModeContactId(typeof tm.contact_id === "string" ? tm.contact_id : "");
          setTestModePhone(typeof tm.phone === "string" ? tm.phone : "");
          setTestModeEmail(typeof tm.email === "string" ? tm.email : "");
        } else {
          setTestModeActivate(false);
          setTestModeContactId("");
          setTestModePhone("");
          setTestModeEmail("");
        }
      }
    } catch (e) {
      console.error("GHL integration GET:", e);
    } finally {
      if (trackLoading) setIsLoadingIntegration(false);
    }
  }, []);

  useEffect(() => {
    loadIntegration({ trackLoading: true });
  }, [loadIntegration]);

  useEffect(() => {
    if (!selectedTimezone && detectedTimezone) {
      if (TIMEZONE_OPTIONS.some((tz) => tz.value === detectedTimezone)) {
        setSelectedTimezone(detectedTimezone);
      }
    }
  }, [detectedTimezone, selectedTimezone]);

  const getStatusBadge = () => {
    if (!hasApiKey) return null;

    const statusConfig = {
      active: {
        bg: "bg-green-500/10",
        border: "border-green-500/30",
        text: "text-green-400",
        label: "Active",
        icon: CheckCircle,
      },
      paused: {
        bg: "bg-yellow-500/10",
        border: "border-yellow-500/30",
        text: "text-yellow-400",
        label: "Paused",
        icon: AlertCircle,
      },
      error: {
        bg: "bg-red-500/10",
        border: "border-red-500/30",
        text: "text-red-400",
        label: "Error",
        icon: AlertCircle,
      },
      pending_auth: {
        bg: "bg-white/5",
        border: "border-white/10",
        text: "text-[#9CA3AF]",
        label: "Pending setup",
        icon: AlertCircle,
      },
    };

    const config = statusConfig[integrationStatus] || statusConfig.pending_auth;
    const Icon = config.icon;

    return (
      <div
        className={`inline-flex items-center gap-2 px-3 py-1.5 rounded-full ${config.bg} ${config.border} border`}
      >
        <Icon className={`w-3.5 h-3.5 ${config.text}`} />
        <span className={`text-xs font-body font-medium ${config.text}`}>{config.label}</span>
      </div>
    );
  };

  const handleToggleStatus = async () => {
    if (!hasApiKey) return;

    setIsTogglingStatus(true);
    setStatusNotification(null);

    try {
      const token = getClientAuthToken();
    if (!token) {
        setStatusNotification({ type: "error", message: "You are not logged in. Please login and try again." });
        setIsTogglingStatus(false);
        return;
      }

      const newStatus = isEnabled ? "paused" : "active";

      const response = await fetch(
        `${BACKEND_URL}/api/integrations/ghl/status?status=${newStatus}`,
        {
          method: "PUT",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (response.ok) {
        const data = await response.json();
        if (data.status === "ok") {
          setIsEnabled(!isEnabled);
          setIntegrationStatus(newStatus);

          const statusLabel = newStatus === "active" ? "enabled" : "disabled";
          setStatusNotification({
            type: "success",
            message: `Integration has been ${statusLabel} successfully`,
          });
          setTimeout(() => setStatusNotification(null), 3000);
        }
      } else {
        const errData = await response.json().catch(() => null);
        setStatusNotification({
          type: "error",
          message: errData?.detail || "Failed to update integration status.",
        });
        setTimeout(() => setStatusNotification(null), 5000);
      }
    } catch (err) {
      console.error("Toggle status error:", err);
      setStatusNotification({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
      setTimeout(() => setStatusNotification(null), 5000);
    } finally {
      setIsTogglingStatus(false);
    }
  };

  const handleSaveTestMode = async () => {
    setTestModeError("");
    setTestModeSuccess(false);
    if (testModeActivate) {
      const hasOne =
        testModeContactId.trim() || testModePhone.trim() || testModeEmail.trim();
      if (!hasOne) {
        setTestModeError("Provide at least one of: contact_id, phone, or email.");
        return;
      }
    }

    setIsSavingTestMode(true);
    try {
      const token = getClientAuthToken();
    if (!token) {
        setTestModeError("Not signed in. Please log in again.");
        return;
      }

      const response = await fetch(GHL_TEST_MODE_SAVE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          activate: testModeActivate,
          contact_id: testModeContactId.trim(),
          phone: testModePhone.trim(),
          email: testModeEmail.trim(),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.status === "ok") {
        setTestModeSuccess(true);
        setTimeout(() => setTestModeSuccess(false), 3000);
        await loadIntegration({ trackLoading: false });
      } else {
        setTestModeError(formatSaveError(data, response.status));
      }
    } catch (e) {
      console.error("GHL test mode save:", e);
      setTestModeError("Network error. Please try again later.");
    } finally {
      setIsSavingTestMode(false);
    }
  };

  const handleOpenTestModal = () => {
    setTestResult(null);
    setIsTestModalOpen(true);
  };

  const handleRunTestConnection = async () => {
    setIsTestingConnection(true);
    setTestResult(null);

    try {
      const token = getClientAuthToken();
    if (!token) {
        setTestResult({ type: "error", message: "Authentication required. Please log in again." });
        setIsTestingConnection(false);
        return;
      }

      const response = await fetch(GHL_TEST_CONNECTION_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok && data.status === "ok") {
        setTestResult({
          type: "success",
          message: data.message || "Connection test succeeded.",
        });
      } else {
        setTestResult({
          type: "error",
          message: formatSaveError(data, response.status),
        });
      }
    } catch (e) {
      console.error("GHL test connection:", e);
      setTestResult({
        type: "error",
        message: "Network error. Please check your connection and try again.",
      });
    } finally {
      setIsTestingConnection(false);
    }
  };

  const loadCalendarModalData = useCallback(async () => {
    const token = getClientAuthToken();
    if (!token) {
      setCalendarModalError("Authentication required. Please log in again.");
      setGhlCalendars([]);
      setTeamCalendarRows([]);
      return;
    }

    setIsCalendarModalLoading(true);
    setCalendarModalError("");
    setCalendarModalSuccess("");

    const headersAuth = { Authorization: `Bearer ${token}` };

    try {
      const [calRes, settingsRes] = await Promise.all([
        fetch(GHL_LIST_CALENDARS_URL, { method: "POST", headers: headersAuth }),
        fetch(BOT_SETTINGS_URL, { method: "GET", headers: headersAuth }),
      ]);

      const calData = await calRes.json().catch(() => ({}));
      if (!calRes.ok) {
        setGhlCalendars([]);
        setCalendarModalError(formatSaveError(calData, calRes.status));
      } else {
        setGhlCalendars(Array.isArray(calData.calendars) ? calData.calendars : []);
      }

      const settingsData = await settingsRes.json().catch(() => ({}));
      if (!settingsRes.ok || settingsData.status !== "ok") {
        setTeamCalendarRows([]);
        setCalendarModalError((prev) =>
          prev || formatSaveError(settingsData, settingsRes.status) || "Failed to load Digital Brain settings."
        );
        return;
      }

      if (!settingsData.has_settings || !settingsData.settings) {
        setTeamCalendarRows([]);
        setCalendarModalError((prev) =>
          prev ||
          "Digital Brain settings not found. Open Digital Brain Settings and complete onboarding (including team calendars) first."
        );
        return;
      }

      const st = settingsData.settings;
      if (st.bot_type !== "roofing_v1" && st.bot_type !== "renovation_v1") {
        setTeamCalendarRows([]);
        setCalendarModalError((prev) =>
          prev ||
          "Calendar mapping is available for roofing_v1 and renovation_v1 Digital Brains only. Change Digital Brain type in Digital Brain Settings."
        );
        return;
      }

      const team = st.scheduling?.team_calendars;
      if (!Array.isArray(team) || team.length === 0) {
        setTeamCalendarRows([]);
        setCalendarModalError((prev) =>
          prev ||
          "No team calendars configured yet. Add team members under Scheduling in Digital Brain Settings, then return here."
        );
        return;
      }

      setCalendarBotType(st.bot_type || "");
      setTeamCalendarRows(
        team.map((t) => {
          const base = t && typeof t === "object" ? { ...t } : {};
          const cid = base.calendar_id != null ? String(base.calendar_id).trim() : "";
          return { ...base, calendar_id: cid };
        })
      );
    } catch (e) {
      console.error("GHL calendar modal load:", e);
      setCalendarModalError("Network error. Please check your connection and try again.");
      setGhlCalendars([]);
      setTeamCalendarRows([]);
    } finally {
      setIsCalendarModalLoading(false);
    }
  }, []);

  const handleOpenCalendarModal = () => {
    setCalendarModalError("");
    setCalendarModalSuccess("");
    setGhlCalendars([]);
    setTeamCalendarRows([]);
    setIsCalendarModalOpen(true);
    loadCalendarModalData();
  };

  const loadPipelineModalData = useCallback(async () => {
    const token = getClientAuthToken();
    if (!token) {
      setPipelineModalError("Authentication required. Please log in again.");
      setGhlPipelines([]);
      return;
    }

    setIsPipelineModalLoading(true);
    setPipelineModalError("");
    setPipelineModalInfo("");

    try {
      const response = await fetch(GHL_LIST_PIPELINES_URL, {
        method: "POST",
        headers: getClientAuthHeaders(),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setGhlPipelines([]);
        setPipelineModalError(formatSaveError(data, response.status));
        return;
      }
      const list = Array.isArray(data.pipelines) ? data.pipelines : [];
      setGhlPipelines(list);
      if (list.length === 0) {
        setPipelineModalInfo(
          "No pipelines returned from GoHighLevel. Check API scopes (opportunities.readonly) or create pipelines in GHL."
        );
        return;
      }

      const savedPid = String(savedOpportunityRouting?.pipeline?.pipeline_id || "").trim();
      const savedSid = String(savedOpportunityRouting?.pipeline?.stage_id || "").trim();
      if (savedPid) {
        const foundPipeline = list.find((p) => p.id === savedPid);
        if (!foundPipeline) {
          setPipelineModalInfo(
            "Saved pipeline is no longer available in GoHighLevel. Please select a pipeline and stage again."
          );
        } else if (savedSid) {
          const stages = Array.isArray(foundPipeline.stages) ? foundPipeline.stages : [];
          if (!stages.some((s) => s.id === savedSid)) {
            setPipelineModalInfo(
              "Saved stage is no longer available for this pipeline. Please select a stage again."
            );
          }
        }
      }
    } catch (e) {
      console.error("GHL pipeline modal load:", e);
      setPipelineModalError("Network error. Please check your connection and try again.");
      setGhlPipelines([]);
    } finally {
      setIsPipelineModalLoading(false);
    }
  }, [savedOpportunityRouting]);

  const resetPipelineCustomFieldState = () => {
    setPipelineModalStep(PIPELINE_MODAL_STEP.pipeline);
    setIsCustomFieldsLoading(false);
    setGhlCustomFields([]);
    setSelectedCustomFieldId("");
    setCustomFieldTypeWarning("");
    setNewCustomFieldName("");
    setNewCustomFieldPlaceholder("");
    setIsCreatingCustomField(false);
    setCustomFieldCreateSuccess("");
  };

  const handleOpenPipelineModal = () => {
    setPipelineModalError("");
    setPipelineModalInfo("");
    setGhlPipelines([]);
    setSelectedPipelineId(String(savedOpportunityRouting?.pipeline?.pipeline_id || "").trim());
    setSelectedStageId(String(savedOpportunityRouting?.pipeline?.stage_id || "").trim());
    resetPipelineCustomFieldState();
    setIsPipelineModalOpen(true);
    loadPipelineModalData();
  };

  const handlePipelineChange = (pipelineId) => {
    setSelectedPipelineId(pipelineId);
    setSelectedStageId("");
  };

  const handleClosePipelineModal = () => {
    setIsPipelineModalOpen(false);
    setPipelineModalError("");
    setPipelineModalInfo("");
    resetPipelineCustomFieldState();
  };

  const loadCustomFields = useCallback(async () => {
    const token = getClientAuthToken();
    if (!token) {
      setPipelineModalError("Authentication required. Please log in again.");
      setGhlCustomFields([]);
      return;
    }

    setIsCustomFieldsLoading(true);
    setPipelineModalError("");
    setCustomFieldCreateSuccess("");

    try {
      const response = await fetch(GHL_LIST_CUSTOM_FIELDS_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({ model: "opportunity" }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setGhlCustomFields([]);
        setPipelineModalError(formatSaveError(data, response.status));
        return;
      }
      const fields = Array.isArray(data.custom_fields) ? data.custom_fields : [];
      setGhlCustomFields(fields);

      const savedFieldId = String(savedOpportunityRouting?.bot_comment_custom_field?.id || "").trim();
      if (savedFieldId) {
        const savedField = fields.find((f) => f.id === savedFieldId);
        if (savedField) {
          const dataType = String(savedField.dataType || "").trim().toUpperCase();
          if (dataType === "LARGE_TEXT") {
            setSelectedCustomFieldId(savedFieldId);
            setCustomFieldTypeWarning("");
          } else {
            setPipelineModalInfo(
              (prev) =>
                prev ||
                "Saved custom field is no longer LARGE_TEXT in GoHighLevel. Please select or create a LARGE_TEXT field."
            );
          }
        } else {
          setPipelineModalInfo(
            (prev) =>
              prev ||
              "Saved custom field is no longer available in GoHighLevel. Please select or create a field."
          );
        }
      }
    } catch (e) {
      console.error("GHL custom fields load:", e);
      setPipelineModalError("Network error. Please check your connection and try again.");
      setGhlCustomFields([]);
    } finally {
      setIsCustomFieldsLoading(false);
    }
  }, [savedOpportunityRouting]);

  const handlePipelineNext = () => {
    if (!selectedPipelineId || !selectedStageId) return;
    setPipelineModalStep(PIPELINE_MODAL_STEP.customField);
    setCustomFieldTypeWarning("");
    setSelectedCustomFieldId("");
    loadCustomFields();
  };

  const handlePipelineBack = () => {
    setPipelineModalStep(PIPELINE_MODAL_STEP.pipeline);
    setPipelineModalError("");
    setCustomFieldTypeWarning("");
    setCustomFieldCreateSuccess("");
  };

  const handleCustomFieldSelect = (fieldId) => {
    const field = ghlCustomFields.find((f) => f.id === fieldId);
    if (!field) {
      setSelectedCustomFieldId("");
      setCustomFieldTypeWarning("");
      return;
    }
    const dataType = String(field.dataType || "").trim().toUpperCase();
    if (dataType !== "LARGE_TEXT") {
      setSelectedCustomFieldId("");
      setCustomFieldTypeWarning(
        "This field type is not supported. Please choose or create a field with dataType LARGE_TEXT."
      );
      return;
    }
    setCustomFieldTypeWarning("");
    setSelectedCustomFieldId(field.id);
  };

  const handleCreateCustomField = async () => {
    const token = getClientAuthToken();
    if (!token) {
      setPipelineModalError("Authentication required. Please log in again.");
      return;
    }
    const nameVal = newCustomFieldName.trim();
    if (!nameVal) {
      setPipelineModalError("Field name is required.");
      return;
    }

    setIsCreatingCustomField(true);
    setPipelineModalError("");
    setCustomFieldCreateSuccess("");

    try {
      const response = await fetch(GHL_CREATE_CUSTOM_FIELD_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name: nameVal,
          placeholder: newCustomFieldPlaceholder.trim() || undefined,
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setPipelineModalError(formatSaveError(data, response.status));
        return;
      }
      setCustomFieldCreateSuccess(data.custom_field?.name ? `Created "${data.custom_field.name}".` : "Custom field created.");
      setNewCustomFieldName("");
      setNewCustomFieldPlaceholder("");
      await loadCustomFields();
      if (data.custom_field?.id) {
        setSelectedCustomFieldId(data.custom_field.id);
        setCustomFieldTypeWarning("");
      }
    } catch (e) {
      console.error("GHL create custom field:", e);
      setPipelineModalError("Network error. Please check your connection and try again.");
    } finally {
      setIsCreatingCustomField(false);
    }
  };

  const handlePipelineDone = async () => {
    const token = getClientAuthToken();
    if (!token) {
      setPipelineModalError("Authentication required. Please log in again.");
      return;
    }
    if (!selectedPipelineId || !selectedStageId || !selectedCustomFieldId || !selectedCustomField) {
      setPipelineModalError("Select a pipeline, stage, and LARGE_TEXT custom field before saving.");
      return;
    }

    setIsPipelineRoutingSaving(true);
    setPipelineModalError("");

    try {
      const response = await fetch(GHL_SAVE_OPPORTUNITY_ROUTING_URL, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          pipeline_id: selectedPipelineId,
          pipeline_name: selectedPipeline?.name ?? "",
          stage_id: selectedStageId,
          stage_name: selectedStage?.name ?? "",
          custom_field_id: selectedCustomField.id,
          custom_field_name: selectedCustomField.name ?? "",
        }),
      });
      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        setPipelineModalError(formatSaveError(data, response.status));
        return;
      }

      setSavedOpportunityRouting({
        pipeline: data.pipeline ?? {
          pipeline_id: selectedPipelineId,
          pipeline_name: selectedPipeline?.name ?? "",
          stage_id: selectedStageId,
          stage_name: selectedStage?.name ?? "",
        },
        bot_comment_custom_field: data.bot_comment_custom_field ?? {
          id: selectedCustomField.id,
          name: selectedCustomField.name ?? "",
        },
      });
      setPipelineRoutingPageSuccess("Opportunity pipeline and Digital Brain comment field saved.");
      handleClosePipelineModal();
      await loadIntegration({ trackLoading: false });
    } catch (e) {
      console.error("GHL save opportunity routing:", e);
      setPipelineModalError("Network error. Please check your connection and try again.");
    } finally {
      setIsPipelineRoutingSaving(false);
    }
  };

  const handleTeamCalendarGhlChange = (index, ghlCalendarId) => {
    setTeamCalendarRows((prev) =>
      prev.map((row, i) => (i === index ? { ...row, calendar_id: ghlCalendarId } : row))
    );
  };

  const handleSaveTeamCalendarMappings = async () => {
    const token = getClientAuthToken();
    if (!token) {
      setCalendarModalError("Authentication required. Please log in again.");
      return;
    }
    if (!teamCalendarRows.length) return;

    setIsCalendarSaving(true);
    setCalendarModalError("");
    setCalendarModalSuccess("");

    try {
      const updates = teamCalendarRows.map((row, index) => ({
        index,
        calendar_id: (row.calendar_id || "").trim(),
      }));

      const calendarsUrl =
        calendarBotType === "renovation_v1"
          ? PUT_RENOVATION_TEAM_CALENDARS_URL
          : PUT_ROOFING_TEAM_CALENDARS_URL;

      const response = await fetch(calendarsUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ updates }),
      });

      const data = await response.json().catch(() => ({}));
      if (response.ok && data.status === "ok") {
        setCalendarModalSuccess(data.message || "Team calendar mappings saved successfully.");
        setTimeout(() => {
          setIsCalendarModalOpen(false);
          setCalendarModalSuccess("");
        }, 1800);
      } else {
        setCalendarModalError(formatSaveError(data, response.status));
      }
    } catch (e) {
      console.error("Save team calendar mappings:", e);
      setCalendarModalError("Network error. Please check your connection and try again.");
    } finally {
      setIsCalendarSaving(false);
    }
  };

  const handleCopy = useCallback(async (text, fieldKey) => {
    if (!text) return;
    const ok = await copyToClipboard(text);
    if (ok) {
      setCopiedField(fieldKey);
      setTimeout(() => setCopiedField(null), 2000);
    }
  }, []);

  const handleSave = async () => {
    setFormError("");
    setSuccess(false);

    if (!apiKey.trim()) {
      setFormError("Please enter your API key");
      return;
    }
    if (!selectedTimezone) {
      setFormError("Please select a time zone");
      return;
    }

    setIsSaving(true);
    try {
      const token = getClientAuthToken();
    if (!token) {
        setFormError("Authentication required. Please log in again.");
        setIsSaving(false);
        return;
      }

      const response = await fetch(GHL_SAVE_URL, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          api_key: apiKey.trim(),
          timezone: selectedTimezone,
          ...(locationId.trim() ? { location_id: locationId.trim() } : {}),
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (response.ok) {
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        const newId = data.id ?? data.integration_id;
        if (newId) setIntegrationId(newId);
        await loadIntegration({ trackLoading: false });
      } else {
        setFormError(formatSaveError(data, response.status));
      }
    } catch (e) {
      console.error("GHL save:", e);
      setFormError("Network error. Please check your connection and try again.");
    } finally {
      setIsSaving(false);
    }
  };

  const webhookHint =
    integrationId || isLoadingIntegration
      ? "Paste this URL into the corresponding webhook in GoHighLevel"
      : "Webhook URLs appear after your integration is created (open this page again after save, or ensure GET /api/integrations/ghl returns an id).";

  return (
    <div className="min-h-screen bg-[#0F1115] flex" data-testid="ghl-integration-page">
      <Sidebar />

      <div className="flex-1 md:ml-64 flex flex-col">
        <main className="flex-1 px-6 py-10">
          <div className="max-w-2xl mx-auto animate-fade-in-up">
            <button
              type="button"
              onClick={() => navigate("/dashboard")}
              className="flex items-center gap-2 text-[#9CA3AF] hover:text-[#F3F4F6] transition-colors mb-6"
            >
              <ArrowLeft className="w-4 h-4" />
              <span className="text-sm font-body">Back to Dashboard</span>
            </button>

            <div className="mb-8">
              <div className="flex items-center gap-3 mb-2">
                <div
                  className="w-10 h-10 rounded-xl flex items-center justify-center"
                  style={{ backgroundColor: `${ACCENT}1A` }}
                >
                  <Building2 className="w-5 h-5" style={{ color: ACCENT }} />
                </div>
                <h1 className="font-heading font-bold text-2xl md:text-3xl text-[#F3F4F6]">
                  GoHighLevel Integration
                </h1>
              </div>
              <p className="text-[#9CA3AF] font-body text-sm">
                Connect GoHighLevel CRM webhooks and API access for your workspace
              </p>
            </div>

            {hasApiKey && (
              <>
                <div className="glass rounded-2xl p-6 border border-white/10 mb-6" data-testid="ghl-current-status-card">
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-4">
                      <span className="text-[#F3F4F6] font-body text-sm">Current Status:</span>
                      {getStatusBadge()}
                    </div>

                    <div className="flex items-center gap-3">
                      <span className="text-[#9CA3AF] text-sm font-body">
                        {isEnabled ? "Enabled" : "Disabled"}
                      </span>
                      <button
                        type="button"
                        onClick={handleToggleStatus}
                        disabled={isTogglingStatus}
                        className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                          isEnabled ? "" : "bg-white/20"
                        } ${isTogglingStatus ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        style={isEnabled ? { backgroundColor: ACCENT } : undefined}
                        data-testid="status-toggle"
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform duration-300 ${
                            isEnabled ? "translate-x-7" : "translate-x-1"
                          }`}
                        />
                        {isTogglingStatus && (
                          <Loader2 className="w-3 h-3 animate-spin absolute top-1.5 left-1/2 -translate-x-1/2 text-[#0F1115]" />
                        )}
                      </button>
                    </div>
                  </div>

                  {statusNotification && (
                    <div
                      className={`mt-4 p-3 rounded-lg flex items-center gap-2 animate-fade-in-up ${
                        statusNotification.type === "success"
                          ? "bg-green-500/10 border border-green-500/30"
                          : "bg-red-500/10 border border-red-500/30"
                      }`}
                      data-testid="status-notification"
                    >
                      {statusNotification.type === "success" ? (
                        <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0" />
                      ) : (
                        <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
                      )}
                      <span
                        className={`text-sm font-body ${
                          statusNotification.type === "success" ? "text-green-400" : "text-red-400"
                        }`}
                      >
                        {statusNotification.message}
                      </span>
                    </div>
                  )}

                  <div className="mt-5 pt-5 border-t border-white/10">
                    <div className="flex flex-wrap gap-3">
                      <button
                        type="button"
                        onClick={handleOpenTestModal}
                        className="flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 font-body text-sm font-medium hover:opacity-90"
                        style={{
                          backgroundColor: `${ACCENT}1A`,
                          borderColor: `${ACCENT}4D`,
                          color: ACCENT,
                        }}
                        data-testid="test-connection-button"
                      >
                        <Zap className="w-4 h-4" />
                        Test Connection
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenCalendarModal}
                        disabled={!canConnectGhlCalendars}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 font-body text-sm font-medium ${
                          canConnectGhlCalendars
                            ? "hover:opacity-90"
                            : "opacity-50 cursor-not-allowed"
                        }`}
                        style={{
                          backgroundColor: `${ACCENT}1A`,
                          borderColor: `${ACCENT}4D`,
                          color: ACCENT,
                        }}
                        data-testid="connect-calendars-button"
                      >
                        <Calendar className="w-4 h-4" />
                        Connect Calendars
                      </button>
                      <button
                        type="button"
                        onClick={handleOpenPipelineModal}
                        disabled={!canConnectGhlPipelines}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg border transition-all duration-300 font-body text-sm font-medium ${
                          canConnectGhlPipelines
                            ? "hover:opacity-90"
                            : "opacity-50 cursor-not-allowed"
                        }`}
                        style={{
                          backgroundColor: `${ACCENT}1A`,
                          borderColor: `${ACCENT}4D`,
                          color: ACCENT,
                        }}
                        data-testid="connect-pipelines-button"
                      >
                        <GitBranch className="w-4 h-4" />
                        Connect Opportunities / Pipeline
                      </button>
                    </div>
                    <p className="text-[#9CA3AF]/70 text-xs font-body mt-2">
                      Test Connection checks the GHL Contacts API. Connect Calendars loads calendars from GHL and maps
                      them to team calendars in your Digital Brain settings (requires Location ID and a roofing_v1 or renovation_v1 Digital Brain with
                      team calendars configured). {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlLeadsSynced} submits the intro form — contacts are created with source{" "}
                      <span className="font-mono text-[#9CA3AF]">fractolabs_widget</span>. Connect Opportunities /
                      Pipeline loads pipelines and stages (opportunities.readonly) and opportunity custom fields
                      (locations/customFields.readonly and locations/customFields.write for listing and creating
                      fields). Required API scopes include{" "}
                      <span className="font-mono text-[#9CA3AF]">contacts.readonly</span>,{" "}
                      <span className="font-mono text-[#9CA3AF]">contacts.write</span>,{" "}
                      <span className="font-mono text-[#9CA3AF]">opportunities.readonly</span>, and{" "}
                      <span className="font-mono text-[#9CA3AF]">opportunities.write</span>. After each Digital Brain
                      conversation, opportunities are synced (opportunities.readonly search + opportunities.write)
                      using the saved pipeline, stage, and comment field — {WEBSITE_LEAD_INTERCEPTOR_COPY.ghlChatsIncluded}
                    </p>
                    {savedOpportunityRouting?.pipeline?.pipeline_id && (
                      <p
                        className="text-xs text-[#9CA3AF] font-body mt-2 rounded-lg bg-white/5 border border-white/10 px-3 py-2"
                        data-testid="ghl-saved-opportunity-routing"
                      >
                        Pipeline:{" "}
                        <span className="text-[#F3F4F6]">
                          {savedOpportunityRouting.pipeline.pipeline_name ||
                            savedOpportunityRouting.pipeline.pipeline_id}
                        </span>
                        {" · "}
                        Stage:{" "}
                        <span className="text-[#F3F4F6]">
                          {savedOpportunityRouting.pipeline.stage_name ||
                            savedOpportunityRouting.pipeline.stage_id}
                        </span>
                        {savedOpportunityRouting.bot_comment_custom_field?.name ||
                        savedOpportunityRouting.bot_comment_custom_field?.id ? (
                          <>
                            {" · "}
                            Digital Brain comments field:{" "}
                            <span className="text-[#F3F4F6]">
                              {savedOpportunityRouting.bot_comment_custom_field.name ||
                                savedOpportunityRouting.bot_comment_custom_field.id}
                            </span>
                          </>
                        ) : null}
                      </p>
                    )}
                    {pipelineRoutingPageSuccess && (
                      <p className="text-emerald-400/90 text-xs font-body mt-2">{pipelineRoutingPageSuccess}</p>
                    )}
                  </div>
                </div>

                <div
                  className="glass rounded-2xl p-6 border border-white/10 mb-6 space-y-4"
                  data-testid="ghl-test-mode-block"
                >
                  <div className="flex items-center justify-between gap-4">
                    <h2 className="font-heading font-bold text-lg text-[#F3F4F6]">Test mode</h2>
                    <div className="flex items-center gap-3 shrink-0">
                      <span className="text-[#9CA3AF] text-xs font-body">
                        {testModeActivate ? "Enabled" : "Disabled"}
                      </span>
                      <button
                        type="button"
                        onClick={() => setTestModeActivate((v) => !v)}
                        disabled={isSavingTestMode}
                        className={`relative w-12 h-6 rounded-full transition-colors duration-300 ${
                          testModeActivate ? "" : "bg-white/20"
                        } ${isSavingTestMode ? "opacity-50 cursor-not-allowed" : "cursor-pointer"}`}
                        style={testModeActivate ? { backgroundColor: ACCENT } : undefined}
                        data-testid="test-mode-toggle"
                      >
                        <div
                          className={`absolute top-1 w-4 h-4 rounded-full bg-white transition-transform duration-300 ${
                            testModeActivate ? "translate-x-7" : "translate-x-1"
                          }`}
                        />
                      </button>
                    </div>
                  </div>
                  <p className="text-[#9CA3AF]/90 text-xs font-body leading-relaxed">
                    When test mode is on, the Digital Brain will only interact with the CRM contact that matches at least one of
                    these values: <span className="text-[#9CA3AF]">contact_id</span>,{" "}
                    <span className="text-[#9CA3AF]">phone</span>, or <span className="text-[#9CA3AF]">email</span>. You
                    only need to set one of them.
                  </p>
                  <div className="space-y-3">
                    <div>
                      <label className="block text-[#9CA3AF] text-xs font-body mb-1">contact_id</label>
                      <input
                        type="text"
                        value={testModeContactId}
                        onChange={(e) => setTestModeContactId(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] font-body text-sm focus:outline-none focus:border-white/20"
                        placeholder=""
                        data-testid="test-mode-contact-id"
                      />
                    </div>
                    <div>
                      <label className="block text-[#9CA3AF] text-xs font-body mb-1">phone</label>
                      <input
                        type="text"
                        value={testModePhone}
                        onChange={(e) => setTestModePhone(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] font-body text-sm focus:outline-none focus:border-white/20"
                        placeholder="+61234567890"
                        data-testid="test-mode-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-[#9CA3AF] text-xs font-body mb-1">email</label>
                      <input
                        type="email"
                        value={testModeEmail}
                        onChange={(e) => setTestModeEmail(e.target.value)}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] font-body text-sm focus:outline-none focus:border-white/20"
                        placeholder="name@example.com"
                        data-testid="test-mode-email"
                      />
                    </div>
                  </div>
                  {testModeError && (
                    <p className="text-red-400 text-xs font-body" data-testid="test-mode-error">
                      {testModeError}
                    </p>
                  )}
                  {testModeSuccess && (
                    <p
                      className="text-green-400 text-xs font-body flex items-center gap-1"
                      data-testid="test-mode-success"
                    >
                      <CheckCircle className="w-3.5 h-3.5" />
                      Saved
                    </p>
                  )}
                  <button
                    type="button"
                    onClick={handleSaveTestMode}
                    disabled={isSavingTestMode}
                    className="px-4 py-2.5 rounded-lg font-body text-sm font-medium text-[#0F1115] transition-opacity disabled:opacity-50"
                    style={{ backgroundColor: ACCENT }}
                    data-testid="test-mode-save"
                  >
                    {isSavingTestMode ? (
                      <span className="inline-flex items-center gap-2">
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving...
                      </span>
                    ) : (
                      "Save"
                    )}
                  </button>
                </div>
              </>
            )}

            <div className="space-y-6">
              {/* Automation: inbound webhooks configured in GHL */}
              <div
                className="glass rounded-2xl p-8 md:p-10 border border-white/10"
                data-testid="ghl-automation-card"
              >
                <div className="mb-8">
                  <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-2">
                    GoHighLevel automation
                  </h2>
                  <p className="text-[#9CA3AF] font-body text-sm">
                    Use these URLs in GoHighLevel Workflows or Automations so events reach FractoLabs. Paste each
                    webhook into the matching trigger or inbound webhook action in GHL.
                  </p>
                </div>

                <div className="space-y-6">
                  <WebhookUrlRow
                    label="Webhook: Contact Created"
                    value={webhooks.contactCreated}
                    isLoading={isLoadingIntegration}
                    hint={webhookHint}
                    copied={copiedField === "contact"}
                    onCopy={() => handleCopy(webhooks.contactCreated, "contact")}
                    testIdPrefix="webhook-contact-created"
                  />

                  <WebhookUrlRow
                    label="Webhook: Appointment Status"
                    value={webhooks.appointmentStatus}
                    isLoading={isLoadingIntegration}
                    hint={webhookHint}
                    copied={copiedField === "appointment"}
                    onCopy={() => handleCopy(webhooks.appointmentStatus, "appointment")}
                    testIdPrefix="webhook-appointment-status"
                  />

                  <WebhookUrlRow
                    label="Webhook: Customer Replied"
                    value={webhooks.customerReplied}
                    isLoading={isLoadingIntegration}
                    hint={webhookHint}
                    copied={copiedField === "customer"}
                    onCopy={() => handleCopy(webhooks.customerReplied, "customer")}
                    testIdPrefix="webhook-customer-replied"
                  />
                </div>
              </div>

              {/* API credentials: key, timezone, persist */}
              <div
                className="glass rounded-2xl p-8 md:p-10 border border-white/10"
                data-testid="ghl-api-card"
              >
                <div className="mb-8">
                  <h2 className="font-heading font-bold text-lg md:text-xl text-[#F3F4F6] mb-2">
                    API key, location &amp; time zone
                  </h2>
                  <p className="text-[#9CA3AF] font-body text-sm">
                    Enter your GoHighLevel API key, optional Location ID, and the time zone used when saving this
                    integration.
                  </p>
                </div>

                {success && (
                  <div
                    className="mb-6 p-4 rounded-lg bg-green-500/10 border border-green-500/30"
                    data-testid="success-message"
                  >
                    <p className="text-green-400 text-sm font-body text-center flex items-center justify-center gap-2">
                      <CheckCircle className="w-5 h-5" />
                      Configuration saved successfully!
                    </p>
                  </div>
                )}

                <div className="space-y-6">
                  <div className="space-y-2">
                    <label className="block text-[#F3F4F6] text-sm font-body">
                      API Key <span className="text-red-400">*</span>
                    </label>
                    <input
                      type="password"
                      autoComplete="off"
                      value={apiKey}
                      onChange={(e) => setApiKey(e.target.value)}
                      placeholder="Enter your GoHighLevel API key"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6] font-body text-sm placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#01B47A]/50 focus:ring-1 focus:ring-[#01B47A]/30 transition-colors"
                      data-testid="api-key-input"
                    />
                    <p className="text-[#9CA3AF]/70 text-xs font-body">
                      From GoHighLevel: Settings → Company → API or your sub-account API key, depending on your setup
                    </p>
                  </div>

                  <div className="space-y-2">
                    <label className="block text-[#F3F4F6] text-sm font-body">Location ID</label>
                    <input
                      type="text"
                      autoComplete="off"
                      value={locationId}
                      onChange={(e) => setLocationId(e.target.value)}
                      placeholder="Sub-account location ID (optional)"
                      className="w-full bg-white/5 border border-white/10 rounded-lg px-4 py-3 text-[#F3F4F6] font-body text-sm placeholder-[#9CA3AF]/50 focus:outline-none focus:border-[#01B47A]/50 focus:ring-1 focus:ring-[#01B47A]/30 transition-colors"
                      data-testid="location-id-input"
                    />
                    <p className="text-[#9CA3AF]/70 text-xs font-body">
                      From GoHighLevel: Settings → Business Info — Location ID for this sub-account (if applicable)
                    </p>
                  </div>

                  <TimezoneField
                    value={selectedTimezone}
                    onChange={setSelectedTimezone}
                    variant="ghl"
                    detectedTimezone={detectedTimezone}
                    footerHintDefault="Used for scheduling logic aligned with your CRM"
                  />

                  {formError && (
                    <div className="flex items-center gap-2">
                      <AlertCircle className="w-3.5 h-3.5 text-red-400 flex-shrink-0" />
                      <p className="text-red-400 text-xs font-body">{formError}</p>
                    </div>
                  )}

                  <button
                    type="button"
                    onClick={handleSave}
                    disabled={!apiKey.trim() || !selectedTimezone || isSaving}
                    className={`w-full font-heading font-bold text-base md:text-lg py-4 px-6 rounded-lg transition-colors duration-300 flex items-center justify-center gap-2 mt-4 ${
                      apiKey.trim() && selectedTimezone && !isSaving
                        ? "btn-pulse text-white hover:opacity-90"
                        : "text-white/70 cursor-not-allowed opacity-60"
                    }`}
                    style={{
                      backgroundColor:
                        apiKey.trim() && selectedTimezone && !isSaving ? ACCENT : `${ACCENT}80`,
                    }}
                    data-testid="save-button"
                  >
                    {isSaving ? (
                      <>
                        <Loader2 className="w-5 h-5 animate-spin" />
                        Saving...
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-5 h-5" />
                        Save
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>

            <div
              className="mt-6 p-4 rounded-lg bg-white/5 border border-white/10"
              data-testid="ghl-help-section"
            >
              <h3 className="font-heading font-semibold text-sm text-[#F3F4F6] mb-2">
                How to connect GoHighLevel
              </h3>
              <ol className="text-[#9CA3AF] text-xs font-body leading-relaxed space-y-1 list-decimal list-inside">
                <li>
                  In the <span className="text-[#F3F4F6] font-medium">GoHighLevel automation</span> card, copy each
                  webhook URL and add it to the matching workflow, automation, or inbound webhook in GHL.
                </li>
                <li>
                  In the <span className="text-[#F3F4F6] font-medium">API key, location &amp; time zone</span> card,
                  paste your API key, optionally your Location ID, select your time zone, and click Save.
                </li>
              </ol>
            </div>
          </div>
        </main>

        <footer className="py-6 px-6 border-t border-white/5">
          <div className="max-w-4xl mx-auto text-center">
            <div className="flex items-center justify-center gap-6 mb-4">
              <a
                href="https://fractolabs.com/privacy-policy"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
              >
                Privacy Policy
              </a>
              <span className="text-[#9CA3AF]/30">|</span>
              <a
                href="https://fractolabs.com/terms"
                target="_blank"
                rel="noopener noreferrer"
                className="text-[#9CA3AF] text-sm hover:text-[#F3F4F6] transition-colors"
              >
                Terms of Service
              </a>
            </div>
            <p className="text-[#9CA3AF]/60 text-xs font-mono">
              © 2026 FractoLabs. All rights reserved.
            </p>
          </div>
        </footer>
      </div>

      {isTestModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="test-connection-modal"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              if (!isTestingConnection) {
                setIsTestModalOpen(false);
                setTestResult(null);
              }
            }}
          />

          <div className="relative w-full max-w-md bg-[#1A1D23] rounded-2xl border border-white/10 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${ACCENT}1A` }}
                >
                  <Zap className="w-4 h-4" style={{ color: ACCENT }} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">Test Connection</h3>
                  <p className="text-[#9CA3AF] text-xs font-body">Verify GHL Contacts API with saved credentials</p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!isTestingConnection) {
                    setIsTestModalOpen(false);
                    setTestResult(null);
                  }
                }}
                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/10 transition-all"
                disabled={isTestingConnection}
                data-testid="close-test-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              <p className="text-[#9CA3AF] text-sm font-body leading-relaxed">
                This runs a single GET to the LeadConnector contacts endpoint with your stored API key and Location ID
                (limit 1). No contact data is shown in the dashboard.
              </p>

              {testResult && (
                <div
                  className={`p-4 rounded-lg flex items-start gap-3 animate-fade-in-up ${
                    testResult.type === "success"
                      ? "bg-green-500/10 border border-green-500/30"
                      : "bg-red-500/10 border border-red-500/30"
                  }`}
                  data-testid="test-result-message"
                >
                  {testResult.type === "success" ? (
                    <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0 mt-0.5" />
                  ) : (
                    <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  )}
                  <p
                    className={`text-sm font-body ${
                      testResult.type === "success" ? "text-green-400" : "text-red-400"
                    }`}
                  >
                    {testResult.message}
                  </p>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10">
              <button
                type="button"
                onClick={() => {
                  if (!isTestingConnection) {
                    setIsTestModalOpen(false);
                    setTestResult(null);
                  }
                }}
                disabled={isTestingConnection}
                className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20 transition-all font-body text-sm"
                data-testid="cancel-test-button"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleRunTestConnection}
                disabled={isTestingConnection}
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-body text-sm font-medium text-white transition-all duration-300 ${
                  !isTestingConnection ? "hover:opacity-90" : "opacity-60 cursor-not-allowed"
                }`}
                style={{ backgroundColor: isTestingConnection ? `${ACCENT}80` : ACCENT }}
                data-testid="run-test-button"
              >
                {isTestingConnection ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Running...
                  </>
                ) : (
                  <>
                    <Zap className="w-4 h-4" />
                    Run test
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isCalendarModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="connect-calendars-modal"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              if (!isCalendarSaving && !isCalendarModalLoading) {
                setIsCalendarModalOpen(false);
                setCalendarModalError("");
                setCalendarModalSuccess("");
              }
            }}
          />

          <div className="relative w-full max-w-2xl max-h-[85vh] overflow-y-auto bg-[#1A1D23] rounded-2xl border border-white/10 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#1A1D23] z-10">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${ACCENT}1A` }}
                >
                  <Calendar className="w-4 h-4" style={{ color: ACCENT }} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">Connect Calendars</h3>
                  <p className="text-[#9CA3AF] text-xs font-body">
                    Map each team calendar to a GoHighLevel calendar for this location
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={() => {
                  if (!isCalendarSaving && !isCalendarModalLoading) {
                    setIsCalendarModalOpen(false);
                    setCalendarModalError("");
                    setCalendarModalSuccess("");
                  }
                }}
                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/10 transition-all"
                disabled={isCalendarSaving || isCalendarModalLoading}
                data-testid="close-connect-calendars-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {isCalendarModalLoading && (
                <div className="flex items-center gap-3 text-[#9CA3AF] text-sm font-body">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} />
                  Loading calendars and Digital Brain settings…
                </div>
              )}

              {calendarModalError && (
                <div
                  className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3"
                  data-testid="connect-calendars-error"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="text-sm text-red-400 font-body space-y-2">
                    <p>{calendarModalError}</p>
                    <button
                      type="button"
                      onClick={() => {
                        navigate(DASHBOARD_ROUTES.settings);
                        setIsCalendarModalOpen(false);
                      }}
                      className="text-[#F3F4F6] underline hover:no-underline text-left"
                    >
                      Open Digital Brain Settings
                    </button>
                  </div>
                </div>
              )}

              {calendarModalSuccess && (
                <div
                  className="p-4 rounded-lg bg-green-500/10 border border-green-500/30 flex items-center gap-3"
                  data-testid="connect-calendars-success"
                >
                  <CheckCircle className="w-5 h-5 text-green-400 flex-shrink-0" />
                  <p className="text-sm text-green-400 font-body">{calendarModalSuccess}</p>
                </div>
              )}

              {!isCalendarModalLoading &&
                teamCalendarRows.length > 0 &&
                ghlCalendars.length === 0 &&
                !calendarModalError && (
                  <p className="text-amber-400/90 text-xs font-body">
                    No calendars were returned from GoHighLevel. Check API scopes or add calendars in GHL; you can
                    still save to clear mappings.
                  </p>
                )}

              {!isCalendarModalLoading && teamCalendarRows.length > 0 && (
                <div className="space-y-4">
                  <p className="text-[#9CA3AF] text-sm font-body">
                    Choose which GHL calendar corresponds to each team member configured in your Digital Brain settings.
                  </p>
                  <div className="rounded-lg border border-white/10 overflow-hidden">
                    <table className="w-full text-sm font-body">
                      <thead>
                        <tr className="bg-white/5 text-left text-[#9CA3AF]">
                          <th className="px-4 py-3 font-medium">Team calendar</th>
                          <th className="px-4 py-3 font-medium">GoHighLevel calendar</th>
                        </tr>
                      </thead>
                      <tbody>
                        {teamCalendarRows.map((row, index) => (
                          <tr key={index} className="border-t border-white/10">
                            <td className="px-4 py-3 text-[#F3F4F6] align-middle">
                              {teamMemberLabel(row, index)}
                            </td>
                            <td className="px-4 py-3 align-middle">
                              <select
                                value={row.calendar_id || ""}
                                onChange={(e) => handleTeamCalendarGhlChange(index, e.target.value)}
                                disabled={isCalendarSaving}
                                className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-[#F3F4F6] focus:outline-none focus:border-[#01B47A]/50"
                                data-testid={`connect-calendars-select-${index}`}
                              >
                                <option value="">— None —</option>
                                {ghlCalendars.map((c) => (
                                  <option key={c.id} value={c.id}>
                                    {c.name} ({c.id})
                                  </option>
                                ))}
                              </select>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>

            <div className="flex items-center justify-end gap-3 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#1A1D23]">
              <button
                type="button"
                onClick={() => {
                  if (!isCalendarSaving) {
                    setIsCalendarModalOpen(false);
                    setCalendarModalError("");
                    setCalendarModalSuccess("");
                  }
                }}
                disabled={isCalendarSaving || isCalendarModalLoading}
                className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20 transition-all font-body text-sm"
                data-testid="connect-calendars-cancel"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleSaveTeamCalendarMappings}
                disabled={
                  isCalendarSaving || isCalendarModalLoading || teamCalendarRows.length === 0
                }
                className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-body text-sm font-medium text-white transition-all duration-300 ${
                  !isCalendarSaving && !isCalendarModalLoading && teamCalendarRows.length > 0
                    ? "hover:opacity-90"
                    : "opacity-60 cursor-not-allowed"
                }`}
                style={{
                  backgroundColor:
                    isCalendarSaving || isCalendarModalLoading || teamCalendarRows.length === 0
                      ? `${ACCENT}80`
                      : ACCENT,
                }}
                data-testid="connect-calendars-save"
              >
                {isCalendarSaving ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Saving…
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    Save
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {isPipelineModalOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center p-4"
          data-testid="connect-pipelines-modal"
        >
          <div
            className="absolute inset-0 bg-black/70 backdrop-blur-sm"
            onClick={() => {
              if (
                !isPipelineModalLoading &&
                !isCustomFieldsLoading &&
                !isCreatingCustomField &&
                !isPipelineRoutingSaving
              ) {
                handleClosePipelineModal();
              }
            }}
          />

          <div className="relative w-full max-w-lg max-h-[85vh] overflow-y-auto bg-[#1A1D23] rounded-2xl border border-white/10 shadow-2xl animate-fade-in-up">
            <div className="flex items-center justify-between p-6 border-b border-white/10 sticky top-0 bg-[#1A1D23] z-10">
              <div className="flex items-center gap-3">
                <div
                  className="w-9 h-9 rounded-lg flex items-center justify-center"
                  style={{ backgroundColor: `${ACCENT}1A` }}
                >
                  <GitBranch className="w-4 h-4" style={{ color: ACCENT }} />
                </div>
                <div>
                  <h3 className="font-heading font-bold text-lg text-[#F3F4F6]">
                    Connect Opportunities / Pipeline
                  </h3>
                  <p className="text-[#9CA3AF] text-xs font-body">
                    {pipelineModalStep === PIPELINE_MODAL_STEP.pipeline
                      ? "Step 1 of 2 · Pipeline and stage"
                      : "Step 2 of 2 · Custom field for Digital Brain comments"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={handleClosePipelineModal}
                className="p-1.5 rounded-lg text-[#9CA3AF] hover:text-[#F3F4F6] hover:bg-white/10 transition-all"
                disabled={
                  isPipelineModalLoading ||
                  isCustomFieldsLoading ||
                  isCreatingCustomField ||
                  isPipelineRoutingSaving
                }
                data-testid="close-connect-pipelines-modal"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 space-y-5">
              {pipelineModalStep === PIPELINE_MODAL_STEP.pipeline && isPipelineModalLoading && (
                <div className="flex items-center gap-3 text-[#9CA3AF] text-sm font-body">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} />
                  Loading pipelines from GoHighLevel…
                </div>
              )}

              {pipelineModalStep === PIPELINE_MODAL_STEP.customField && isCustomFieldsLoading && (
                <div className="flex items-center gap-3 text-[#9CA3AF] text-sm font-body">
                  <Loader2 className="w-5 h-5 animate-spin" style={{ color: ACCENT }} />
                  Loading opportunity custom fields…
                </div>
              )}

              {pipelineModalError && (
                <div
                  className="p-4 rounded-lg bg-red-500/10 border border-red-500/30 flex items-start gap-3"
                  data-testid="connect-pipelines-error"
                >
                  <AlertCircle className="w-5 h-5 text-red-400 flex-shrink-0 mt-0.5" />
                  <p className="text-sm text-red-400 font-body">{pipelineModalError}</p>
                </div>
              )}

              {pipelineModalStep === PIPELINE_MODAL_STEP.customField &&
                pipelineModalError &&
                ghlCustomFields.length === 0 && (
                  <p
                    className="text-[#9CA3AF] text-xs font-body"
                    data-testid="connect-pipelines-custom-fields-scope-hint"
                  >
                    Required scopes: locations/customFields.readonly (list fields), locations/customFields.write
                    (create fields).
                  </p>
                )}

              {pipelineModalStep === PIPELINE_MODAL_STEP.pipeline &&
                !isPipelineModalLoading &&
                !pipelineModalError && (
                  <>
                    <p className="text-[#9CA3AF] text-sm font-body leading-relaxed">
                      Select a pipeline and stage. After a conversation with the Digital Brain, leads will be moved to this
                      stage.
                    </p>

                    {pipelineModalInfo && (
                      <p className="text-amber-400/90 text-xs font-body" data-testid="connect-pipelines-info">
                        {pipelineModalInfo}
                      </p>
                    )}

                    {ghlPipelines.length > 0 && (
                      <div className="space-y-4">
                        <div>
                          <label
                            htmlFor="ghl-pipeline-select"
                            className="block text-sm font-medium text-[#F3F4F6] font-body mb-2"
                          >
                            Pipeline
                          </label>
                          <select
                            id="ghl-pipeline-select"
                            value={selectedPipelineId}
                            onChange={(e) => handlePipelineChange(e.target.value)}
                            disabled={isPipelineModalLoading}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-[#F3F4F6] focus:outline-none focus:border-[#01B47A]/50"
                            data-testid="connect-pipelines-pipeline-select"
                          >
                            <option value="">Select a pipeline…</option>
                            {ghlPipelines.map((p) => (
                              <option key={p.id} value={p.id}>
                                {p.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        <div>
                          <label
                            htmlFor="ghl-stage-select"
                            className="block text-sm font-medium text-[#F3F4F6] font-body mb-2"
                          >
                            Stage
                          </label>
                          <select
                            id="ghl-stage-select"
                            value={selectedStageId}
                            onChange={(e) => setSelectedStageId(e.target.value)}
                            disabled={isPipelineModalLoading || !selectedPipelineId}
                            className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-[#F3F4F6] focus:outline-none focus:border-[#01B47A]/50 disabled:opacity-50"
                            data-testid="connect-pipelines-stage-select"
                          >
                            <option value="">Select a stage…</option>
                            {stagesForSelectedPipeline.map((s) => (
                              <option key={s.id} value={s.id}>
                                {s.name}
                              </option>
                            ))}
                          </select>
                        </div>

                        {selectedPipeline && selectedStage && (
                          <p
                            className="text-xs text-[#9CA3AF] font-body rounded-lg bg-white/5 border border-white/10 px-3 py-2"
                            data-testid="connect-pipelines-preview"
                          >
                            Pipeline: <span className="text-[#F3F4F6]">{selectedPipeline.name}</span>
                            {" · "}
                            Stage: <span className="text-[#F3F4F6]">{selectedStage.name}</span>
                          </p>
                        )}
                      </div>
                    )}
                  </>
                )}

              {pipelineModalStep === PIPELINE_MODAL_STEP.customField && !isCustomFieldsLoading && (
                  <>
                    <p className="text-[#9CA3AF] text-sm font-body leading-relaxed">
                      Select a custom field where the Digital Brain will add comments. The field must have{" "}
                      <span className="text-[#F3F4F6] font-medium">dataType = LARGE_TEXT</span>.
                    </p>

                    {selectedPipeline && selectedStage && (
                      <p
                        className="text-xs text-[#9CA3AF] font-body rounded-lg bg-white/5 border border-white/10 px-3 py-2"
                        data-testid="connect-pipelines-step2-pipeline-summary"
                      >
                        Pipeline: <span className="text-[#F3F4F6]">{selectedPipeline.name}</span>
                        {" · "}
                        Stage: <span className="text-[#F3F4F6]">{selectedStage.name}</span>
                      </p>
                    )}

                    <div>
                      <label
                        htmlFor="ghl-custom-field-select"
                        className="block text-sm font-medium text-[#F3F4F6] font-body mb-2"
                      >
                        Custom field
                      </label>
                      <select
                        id="ghl-custom-field-select"
                        value={selectedCustomFieldId}
                        onChange={(e) => handleCustomFieldSelect(e.target.value)}
                        disabled={isCustomFieldsLoading || isCreatingCustomField}
                        className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-[#F3F4F6] focus:outline-none focus:border-[#01B47A]/50 disabled:opacity-50"
                        data-testid="connect-pipelines-custom-field-select"
                      >
                        <option value="">Select a custom field…</option>
                        {ghlCustomFields.map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name} ({f.dataType || "unknown"})
                          </option>
                        ))}
                      </select>
                    </div>

                    {customFieldTypeWarning && (
                      <div
                        className="p-4 rounded-lg bg-amber-500/10 border border-amber-500/30 flex items-start gap-3"
                        data-testid="connect-pipelines-type-warning"
                      >
                        <AlertCircle className="w-5 h-5 text-amber-400 flex-shrink-0 mt-0.5" />
                        <p className="text-sm text-amber-400 font-body">{customFieldTypeWarning}</p>
                      </div>
                    )}

                    {selectedCustomField && (
                      <p
                        className="text-xs text-[#9CA3AF] font-body rounded-lg bg-white/5 border border-white/10 px-3 py-2"
                        data-testid="connect-pipelines-custom-field-preview"
                      >
                        Field: <span className="text-[#F3F4F6]">{selectedCustomField.name}</span>
                        {selectedCustomField.fieldKey ? (
                          <>
                            {" "}
                            (<span className="text-[#F3F4F6]">{selectedCustomField.fieldKey}</span>)
                          </>
                        ) : null}
                      </p>
                    )}

                    {customFieldCreateSuccess && (
                      <p
                        className="text-emerald-400/90 text-xs font-body rounded-lg bg-emerald-500/10 border border-emerald-500/30 px-3 py-2"
                        data-testid="connect-pipelines-create-success"
                      >
                        {customFieldCreateSuccess}
                      </p>
                    )}

                    <div className="pt-4 border-t border-white/10 space-y-4">
                      <p className="text-sm font-medium text-[#F3F4F6] font-body">Create new field</p>
                      <p className="text-xs text-[#9CA3AF] font-body">
                        New fields are created as LARGE_TEXT on the opportunity model.
                      </p>
                      <div>
                        <label
                          htmlFor="ghl-new-custom-field-name"
                          className="block text-sm font-medium text-[#F3F4F6] font-body mb-2"
                        >
                          Field name
                        </label>
                        <input
                          id="ghl-new-custom-field-name"
                          type="text"
                          value={newCustomFieldName}
                          onChange={(e) => setNewCustomFieldName(e.target.value)}
                          disabled={isCreatingCustomField}
                          placeholder="e.g. Digital Brain conversation notes"
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#01B47A]/50 disabled:opacity-50"
                          data-testid="connect-pipelines-create-name"
                        />
                      </div>
                      <div>
                        <label
                          htmlFor="ghl-new-custom-field-placeholder"
                          className="block text-sm font-medium text-[#F3F4F6] font-body mb-2"
                        >
                          Placeholder <span className="text-[#9CA3AF] font-normal">(optional)</span>
                        </label>
                        <input
                          id="ghl-new-custom-field-placeholder"
                          type="text"
                          value={newCustomFieldPlaceholder}
                          onChange={(e) => setNewCustomFieldPlaceholder(e.target.value)}
                          disabled={isCreatingCustomField}
                          placeholder="Shown inside the field in GHL"
                          className="w-full bg-white/5 border border-white/10 rounded-lg px-3 py-2.5 text-[#F3F4F6] placeholder:text-[#6B7280] focus:outline-none focus:border-[#01B47A]/50 disabled:opacity-50"
                        />
                      </div>
                      <button
                        type="button"
                        onClick={handleCreateCustomField}
                        disabled={isCreatingCustomField || !newCustomFieldName.trim()}
                        className={`flex items-center gap-2 px-4 py-2.5 rounded-lg font-body text-sm font-medium text-white transition-all duration-300 ${
                          !isCreatingCustomField && newCustomFieldName.trim()
                            ? "hover:opacity-90"
                            : "opacity-60 cursor-not-allowed"
                        }`}
                        style={{
                          backgroundColor:
                            !isCreatingCustomField && newCustomFieldName.trim() ? ACCENT : `${ACCENT}80`,
                        }}
                        data-testid="connect-pipelines-create-submit"
                      >
                        {isCreatingCustomField ? (
                          <>
                            <Loader2 className="w-4 h-4 animate-spin" />
                            Creating…
                          </>
                        ) : (
                          "Create field"
                        )}
                      </button>
                    </div>
                  </>
                )}
            </div>

            <div className="flex items-center justify-between gap-3 px-6 py-4 border-t border-white/10 sticky bottom-0 bg-[#1A1D23]">
              {pipelineModalStep === PIPELINE_MODAL_STEP.customField ? (
                <button
                  type="button"
                  onClick={handlePipelineBack}
                  disabled={isCustomFieldsLoading || isCreatingCustomField || isPipelineRoutingSaving}
                  className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20 transition-all font-body text-sm disabled:opacity-50"
                  data-testid="connect-pipelines-back"
                >
                  Back
                </button>
              ) : (
                <span />
              )}
              <div className="flex items-center gap-3">
                <button
                  type="button"
                  onClick={handleClosePipelineModal}
                  disabled={
                    isPipelineModalLoading ||
                    isCustomFieldsLoading ||
                    isCreatingCustomField ||
                    isPipelineRoutingSaving
                  }
                  className="px-4 py-2.5 rounded-lg border border-white/10 text-[#9CA3AF] hover:text-[#F3F4F6] hover:border-white/20 transition-all font-body text-sm"
                  data-testid="connect-pipelines-cancel"
                >
                  Cancel
                </button>
                {pipelineModalStep === PIPELINE_MODAL_STEP.pipeline && (
                  <button
                    type="button"
                    onClick={handlePipelineNext}
                    disabled={isPipelineModalLoading || !selectedPipelineId || !selectedStageId}
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-body text-sm font-medium text-white transition-all duration-300 ${
                      !isPipelineModalLoading && selectedPipelineId && selectedStageId
                        ? "hover:opacity-90"
                        : "opacity-60 cursor-not-allowed"
                    }`}
                    style={{
                      backgroundColor:
                        !isPipelineModalLoading && selectedPipelineId && selectedStageId
                          ? ACCENT
                          : `${ACCENT}80`,
                    }}
                    data-testid="connect-pipelines-next"
                  >
                    Next
                  </button>
                )}
                {pipelineModalStep === PIPELINE_MODAL_STEP.customField && (
                  <button
                    type="button"
                    onClick={handlePipelineDone}
                    disabled={
                      isCustomFieldsLoading ||
                      isCreatingCustomField ||
                      isPipelineRoutingSaving ||
                      !selectedCustomFieldId
                    }
                    className={`flex items-center gap-2 px-5 py-2.5 rounded-lg font-body text-sm font-medium text-white transition-all duration-300 ${
                      !isCustomFieldsLoading &&
                      !isCreatingCustomField &&
                      !isPipelineRoutingSaving &&
                      selectedCustomFieldId
                        ? "hover:opacity-90"
                        : "opacity-60 cursor-not-allowed"
                    }`}
                    style={{
                      backgroundColor:
                        !isCustomFieldsLoading &&
                        !isCreatingCustomField &&
                        !isPipelineRoutingSaving &&
                        selectedCustomFieldId
                          ? ACCENT
                          : `${ACCENT}80`,
                    }}
                    data-testid="connect-pipelines-done"
                  >
                    {isPipelineRoutingSaving ? (
                      <>
                        <Loader2 className="w-4 h-4 animate-spin" />
                        Saving…
                      </>
                    ) : (
                      <>
                        <CheckCircle className="w-4 h-4" />
                        Done
                      </>
                    )}
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default GoHighLevel;
